// React Dependencies
import React, { useEffect, useMemo, useRef, useState } from 'react';

// External Dependencies
import { Dialog, IconButton, makeStyles, TextField, Tooltip } from '@material-ui/core';
import { CloudUpload as CloudUploadIcon, Edit as EditIcon } from '@material-ui/icons';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

// Internal Dependencies
import MText from '../../../../../components/Common/MText';
import Constants, { IMAGE_FORMATS, Status } from '../../../../../common/constants';
import Colours from '../../../../../styles/colours';
import withLoyaltyConnect from '../WithLoyaltyConnect';
import LoyaltyMenuDescriptionBox from '../../../../../components/LoyaltyMenuDescriptionBox';
import { IsEmpty } from '../../../../../common/checks';
import ApiService from '../../../../../services/apiService';
import PhotoCropper from '../../../../../components/Common/photos/PhotoCropper';

const useStyles = makeStyles(() => ({
  noSpinner: {
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin:               0,
    },
    '& input[type=number]': {
      '-moz-appearance': 'textfield',
    },
  },
  inputField: {
    width:     120,
    '& input': {
      height:    22,
      fontSize:  12,
      textAlign: 'center',
      padding:   '6px 8px',
    },
  },
}));

const defaultCropperState = {
  cropImage:  { file: null, data: null },
  isCropping: false,
};

const StampsMechanics = (props) => {
  const classes = useStyles();
  const { pageId, loyaltyCardId } = props.match.params;

  const [cropperState, setCropperState] = useState({ ...defaultCropperState });
  const redeemStampImageRef = useRef(null);

  const isDraft = props.loyaltyProgram?.status === Status.Draft;
  const isDeactivated = props.loyaltyProgram?.status === Status.Deactivated;
  const numOfStamps = props.loyaltyProgram?.stamp?.numOfStamps;
  const amountPerStamp = props.loyaltyProgram?.stamp?.amountPerStamp;

  const FormHelperTextProps = {
    style: {
      fontSize:   10,
      marginLeft: 0,
      width:      '100%',
    },
  };

  const isNumOfStampsError = useMemo(() => !numOfStamps || numOfStamps < 1 || numOfStamps > 10 || !Number.isInteger(numOfStamps), [numOfStamps]);

  const isAmountPerStampError = useMemo(() => {
    if (IsEmpty(amountPerStamp)) {
      return true;
    }

    const valueString = amountPerStamp.toString();
    return !/^\d+(\.\d{1,2})?$/.test(valueString) || parseFloat(amountPerStamp) <= 0;
  }, [amountPerStamp]);

  const isError = isNumOfStampsError || isAmountPerStampError;

  const handleCurrencyChange = (e) => {
    const { value } = e.target;

    if (IsEmpty(value) || /^[0-9]*\.?[0-9]{0,2}$/.test(value)) {
      props.updateLoyaltyCardAction({
        stamp: {
          ...props.loyaltyProgram?.stamp,
          amountPerStamp: IsEmpty(value) ? '' : Number(value),
        },
      });
    }
  };

  const handleNumOfStampsChange = (e) => {
    const { value } = e.target;
    const stampValue = IsEmpty(value) ? '' : Number(value);

    props.updateLoyaltyCardAction({
      stamp: {
        ...props.loyaltyProgram?.stamp,
        numOfStamps: stampValue,
      },
    });
  };

  const fileValidated = (file) => {
    if (!IMAGE_FORMATS.includes(file.type)) {
      toast.error('Only JPG, JPEG and PNG files are supported.');
      return false;
    }
    if (file.size > Constants.BinaryOneMBSize * 7) {
      toast.error('Maximum file size of 7MB exceeded.');
      return false;
    }
    return true;
  };

  const handleRedeemStampUpload = async (file) => {
    try {
      props.dispatchStartSavingLC();
      if (IsEmpty(file)) {
        setCropperState({ ...defaultCropperState });
        return;
      }

      const formData = new FormData();
      formData.append('image', file);

      const imageUrl = await ApiService.uploadRedeemStampMedia({ pageId, loyaltyCardId }, formData);
      setCropperState({ ...defaultCropperState, redeemImage: { data: imageUrl } });
      props.updateLoyaltyCardAction({ stamp: { ...props.loyaltyProgram.stamp, redeemStampImage: imageUrl } });

      toast.success('Redeemed stamp image updated');
    } catch (error) {
      toast.error(error.message);
    } finally {
      props.dispatchFinishSavingLC();
    }
  };

  const handleImageChange = async (e) => {
    const fileInput = e.target;
    if (fileInput.files.length === 0) {
      return;
    }
    const file = fileInput.files[0];
    if (!fileValidated(file)) {
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const imageObj = { file, data: reader.result };
      const cropperTriggered = { isCropping: true, cropImage: imageObj };
      setCropperState({ ...cropperTriggered });

      // clear file input
      redeemStampImageRef.current.value = '';
    };
    reader.readAsDataURL(file);
  };

  const closeCropDialog = () => {
    setCropperState({ ...defaultCropperState });
  };

  useEffect(() => {
    if (isError) {
      props.disableSaveBtnAction();
    } else {
      props.enableSaveBtnAction();
    }
  }, [isError]);

  useEffect(() => () => {
    props.enableSaveBtnAction();
  }, []);

  return (
    <div className="container-fluid px-0 pt-3">
      <LoyaltyMenuDescriptionBox
        text="Set the amount spent by users to get 1 stamp on the loyalty card (eg. $5 spent gets 1 stamp, ie. receipt with a $22 spend will allow users to collect 4 stamps). Also determine how many stamps need to be collected to claim a reward."
      />
      <MText className="mb-3 mt-4" size={13} bold colour={Colours.Gray1} text="Loyalty Card Stamp Settings" />

      <div className="d-flex align-items-start mb-3 ml-1">
        <div style={{ width: 200 }} className="d-flex align-items-start">
          <MText size={12}>
            Set the number of stamps per card (Max
            {' '}
            <MText size={12} bold style={{ display: 'inline' }}>10</MText>
            )
          </MText>
        </div>
        <div className="ml-3">
          <TextField
            type="number"
            value={IsEmpty(numOfStamps) ? '' : numOfStamps}
            inputProps={{ min: 1, max: 10 }}
            onChange={handleNumOfStampsChange}
            variant="outlined"
            size="small"
            className={`${classes.inputField} ${classes.noSpinner}`}
            disabled={!isDraft}
            error={isNumOfStampsError}
            helperText={isNumOfStampsError ? 'Required. Min. 1 max. 10' : ''}
            FormHelperTextProps={FormHelperTextProps}
          />
        </div>
      </div>

      <div className="d-flex align-items-start mb-4 ml-1">
        <div style={{ width: 200 }} className="d-flex align-items-start">
          <MText size={12}>Set the amount spent to get 1 stamp ($)</MText>
        </div>
        <div className="ml-3">
          <TextField
            type="number"
            value={props.loyaltyProgram?.stamp?.amountPerStamp}
            onChange={handleCurrencyChange}
            variant="outlined"
            size="small"
            className={`${classes.inputField} ${classes.noSpinner}`}
            disabled={!isDraft}
            error={isAmountPerStampError}
            helperText={isAmountPerStampError ? 'Required. Must be more than 0.' : ''}
            FormHelperTextProps={FormHelperTextProps}
          />
        </div>
      </div>

      <div className="d-flex flex-column align-items-start">
        <div className="d-flex flex-column align-items-center">
          <MText text="Redeemed stamp image" size={13} bold colour={Colours.Gray1} />
          <div className="mt-2 d-flex flex-column align-items-center mr-2">
            {
              !IsEmpty(props.loyaltyProgram.stamp.redeemStampImage) ? (
                <img
                  src={props.loyaltyProgram.stamp.redeemStampImage}
                  alt="Redeemed stamp"
                  style={{ width: 90, height: 90 }}
                  className="rounded-circle"
                />
              ) : <div className="rounded-circle" style={{ backgroundColor: Colours.Gray4, width: 90, height: 90 }} />
            }
            {
              !isDeactivated && (
                <div className="d-flex mt-2">
                  <div>
                    <input
                      hidden
                      name="redeemImage"
                      accept={IMAGE_FORMATS}
                      type="file"
                      onChange={handleImageChange}
                      ref={redeemStampImageRef}
                    />
                    <Tooltip title="Upload">
                      <IconButton
                        disabled={isDeactivated}
                        onClick={() => redeemStampImageRef.current.click()}
                        className="shadow-sm bg-white"
                      >
                        { !IsEmpty(props.loyaltyProgram.stamp.redeemStampImage) ? <EditIcon /> : <CloudUploadIcon />}
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              )
            }
          </div>
        </div>
      </div>

      <Dialog
        fullScreen
        open={cropperState.isCropping}
        onClose={closeCropDialog}
      >
        <PhotoCropper
          width={4}
          height={3}
          cropPixelWidth={400}
          cropPixelHeight={400}
          outputPixelWidth={900}
          outputPixelHeight={900}
          type="upload"
          label="Logo"
          src={cropperState.cropImage.data}
          file={cropperState.cropImage.file}
          handleSave={handleRedeemStampUpload}
          handleClose={closeCropDialog}
          isCropping={cropperState.isCropping}
        />
      </Dialog>
    </div>
  );
};

StampsMechanics.propTypes = {
  match:          PropTypes.shape(Object).isRequired,
  loyaltyProgram: PropTypes.shape({
    stamp: PropTypes.shape({
      numOfStamps:      PropTypes.number,
      amountPerStamp:   PropTypes.number,
      redeemStampImage: PropTypes.string,
    }),
    status: PropTypes.oneOf([Status.Draft, Status.Live, Status.Deactivated]),
  }).isRequired,
  updateLoyaltyCardAction: PropTypes.func.isRequired,
  dispatchStartSavingLC:   PropTypes.func.isRequired,
  dispatchFinishSavingLC:  PropTypes.func.isRequired,
  disableSaveBtnAction:    PropTypes.func.isRequired,
  enableSaveBtnAction:     PropTypes.func.isRequired,
};

export default withLoyaltyConnect(StampsMechanics);
